import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, MapPin, User, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface InterviewReschedulerProps {
  interview: {
    id: string;
    interview_type: string;
    scheduled_date: string;
    interview_data: any;
  };
  candidateName: string;
  onRescheduled: () => void;
  onCancel: () => void;
}

export function InterviewRescheduler({ 
  interview,
  candidateName, 
  onRescheduled, 
  onCancel 
}: InterviewReschedulerProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scheduledDate: '',
    scheduledTime: '',
    location: '',
    interviewer: '',
    notes: '',
    duration: '60' // minutes
  });

  // Initialize form with existing interview data
  useEffect(() => {
    if (interview) {
      const existingDate = new Date(interview.scheduled_date);
      setFormData({
        scheduledDate: existingDate.toISOString().split('T')[0],
        scheduledTime: existingDate.toTimeString().slice(0, 5),
        location: interview.interview_data?.location || '',
        interviewer: interview.interview_data?.interviewer || '',
        notes: interview.interview_data?.notes || '',
        duration: interview.interview_data?.duration?.toString() || '60'
      });
    }
  }, [interview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time
      const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      
      // Update interview record
      const { error } = await supabase
        .from('interviews')
        .update({
          scheduled_date: scheduledDateTime.toISOString(),
          interview_data: {
            location: formData.location,
            interviewer: formData.interviewer,
            duration: parseInt(formData.duration),
            notes: formData.notes
          }
        })
        .eq('id', interview.id);

      if (error) throw error;

      toast({
        title: "Επιτυχία",
        description: `Η ${interview.interview_type === 'first' ? 'πρώτη' : 'δεύτερη'} συνέντευξη επαναπρογραμματίστηκε επιτυχώς`,
      });

      onRescheduled();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Επαναπρογραμματισμός {interview.interview_type === 'first' ? 'Πρώτης' : 'Δεύτερης'} Συνέντευξης
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Υποψήφιος: <span className="font-medium">{candidateName}</span>
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Ημερομηνία
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({...formData, scheduledDate: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ώρα
              </Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({...formData, scheduledTime: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Τοποθεσία
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="π.χ. Γραφείο HR, Αίθουσα συσκέψεων Α'"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="interviewer" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Συνεντευκτής
              </Label>
              <Input
                id="interviewer"
                value={formData.interviewer}
                onChange={(e) => setFormData({...formData, interviewer: e.target.value})}
                placeholder="Ονοματεπώνυμο συνεντευκτή"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">
                Διάρκεια (λεπτά)
              </Label>
              <Input
                id="duration"
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                min="15"
                max="180"
                step="15"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">
              Σημειώσεις (προαιρετικό)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Πρόσθετες πληροφορίες ή οδηγίες για τη συνέντευξη"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Αποθήκευση...' : 'Επαναπρογραμματισμός'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}